import { memo, useEffect } from 'react'
import Box from './Box'
import { Controller, UseFormReturn } from 'react-hook-form'
import { TextFieldWithLabelForm } from './TextFieldWithLabelForm'
import { PhoneInputWithFlag } from './PhoneInputWithFlag'
import { CourseRegistrationFormValues, isValidEmail } from '../helpers/CourseRegistrationStepOneHelper'
import styled from '@emotion/styled'
import { ContinueButton } from './StyledComponents'
import { State } from '../hooks/useAppState'
import iconCompleted from '/public/iconCompleted.svg'
import iconArrow from '/public/iconArrow.svg'
import { EIGHT_PX, FOUR_PX, TWELVE_PX } from '../enums/Spacings'
import { Checkbox, Radio, RadioGroup, FormControlLabel, FormControl } from '@mui/material'
import { getGrooverText } from '../libs/GrooverTextRepository'
import { useGrooverText } from '../hooks/useGrooverText'
import { Client } from '../../../pure-js/types/GrooverTypes'
import { styled as muiStyled } from '@mui/material/styles'
import { BorderRadixes } from '../enums/BorderRadixes'
import { Clear } from '@mui/icons-material'
import { activityHasActiveDiscountOfType, activityOffersStudentPricing } from '../../../pure-js/libs/DiscountHelper'
import { DiscountUserInputV1, DiscountUserInputV2 } from '../../../pure-js/types/DiscountTypes'
import { DanceRoles } from '../../../pure-js/types/GrooverTypes'
import GrooverText from './GrooverText'
import Spinner from './Spinner'
import { Activity } from '../../../pure-js/types/GrooverTypes'

// Type guard to check if the input is DiscountUserInputV2
const isDiscountUserInputV2 = (
  input: DiscountUserInputV1 | DiscountUserInputV2 | undefined
): input is DiscountUserInputV2 => {
  return input !== undefined && 'mainUser' in input
}

type Props = {
  state: State
  client?: Client
  activity: Activity
  formProps: UseFormReturn<CourseRegistrationFormValues, any, CourseRegistrationFormValues>
  partnerRole: DanceRoles | null
  changeRole: (isPartnerRole: boolean, newRole: DanceRoles) => void
  handleRemovePartner: () => void
  disableRemovePartner?: boolean
  onNextStep: () => void
  onSubmit: (data: CourseRegistrationFormValues) => void
  onSetIsPartnerStudent: (isPartnerStudent: boolean) => void
  onSetIsPartnerSibling: (isPartnerSibling: boolean) => void
  onSetIsPartnerSenior: (isPartnerSenior: boolean) => void
  onMainUserPhoneCountryChange?: (country: string) => void
  onPartnerPhoneCountryChange?: (country: string) => void
  isCheckingUnfinishedEnrollment?: boolean
}

const CourseRegistrationStepOnePartnerForm = ({
  state,
  client,
  activity,
  formProps,
  partnerRole,
  changeRole,
  handleRemovePartner,
  disableRemovePartner,
  onNextStep,
  onSubmit,
  onSetIsPartnerStudent,
  onSetIsPartnerSibling,
  onSetIsPartnerSenior,
  onPartnerPhoneCountryChange,
  isCheckingUnfinishedEnrollment = false
}: Props) => {
  const t = useGrooverText()

  const activityHasStudentDiscount = activityOffersStudentPricing(activity, client?.discounts)
  const activityHasSiblingDiscount = activityHasActiveDiscountOfType(activity, client?.discounts, 'sibling')
  const activityHasSeniorDiscount = activityHasActiveDiscountOfType(activity, client?.discounts, 'senior')

  useEffect(() => {
    // Hide + clear when the activity doesn't allow these discounts
    if (
      state.isRegistrationCompleted ||
      state.isRegistrationSubmitted ||
      !!state.obfuscatedEnrollment?.partnerUser?.phone
    ) {
      return
    }

    if (!activityHasStudentDiscount && state.isPartnerStudent) {
      onSetIsPartnerStudent(false)
    }

    if (!activityHasSiblingDiscount && state.isPartnerSibling) {
      onSetIsPartnerSibling(false)
    }

    if (!activityHasSeniorDiscount && state.isPartnerSenior) {
      onSetIsPartnerSenior(false)
    }
  }, [
    activityHasStudentDiscount,
    activityHasSiblingDiscount,
    activityHasSeniorDiscount,
    state.isRegistrationCompleted,
    state.isRegistrationSubmitted,
    state.obfuscatedEnrollment?.partnerUser?.phone,
    state.isPartnerStudent,
    state.isPartnerSibling,
    state.isPartnerSenior,
    onSetIsPartnerStudent,
    onSetIsPartnerSibling,
    onSetIsPartnerSenior
  ])

  const handleSelectStudentDiscount = () => {
    if (
      state.isRegistrationCompleted ||
      state.isRegistrationSubmitted ||
      state.obfuscatedEnrollment?.partnerUser?.phone
    )
      return
    if (state.isPartnerStudent) return onSetIsPartnerStudent(false)
    onSetIsPartnerStudent(true)
    onSetIsPartnerSenior(false)
  }

  const handleSelectSiblingDiscount = () => {
    if (
      state.isRegistrationCompleted ||
      state.isRegistrationSubmitted ||
      state.obfuscatedEnrollment?.partnerUser?.phone
    )
      return
    onSetIsPartnerSibling(!state.isPartnerSibling)
  }

  const handleSelectSeniorDiscount = () => {
    if (
      state.isRegistrationCompleted ||
      state.isRegistrationSubmitted ||
      state.obfuscatedEnrollment?.partnerUser?.phone
    )
      return
    if (state.isPartnerSenior) return onSetIsPartnerSenior(false)
    onSetIsPartnerStudent(false)
    onSetIsPartnerSenior(true)
  }

  const getStudentStatus = () => {
    const input = state.obfuscatedEnrollment?.discountUserInput
    if (!input) return state.isPartnerStudent
    if (isDiscountUserInputV2(input)) {
      return input.partnerUser?.isStudent || state.isPartnerStudent
    }
    return input.isStudent || state.isPartnerStudent
  }

  const getSiblingStatus = () => {
    const input = state.obfuscatedEnrollment?.discountUserInput
    if (!input) return state.isPartnerSibling
    if (isDiscountUserInputV2(input)) {
      return input.partnerUser?.isSibling || state.isPartnerSibling
    }
    return state.isPartnerSibling
  }

  const getSeniorStatus = () => {
    const input = state.obfuscatedEnrollment?.discountUserInput
    if (!input) return state.isPartnerSenior
    if (isDiscountUserInputV2(input)) {
      return input.partnerUser?.isSenior || state.isPartnerSenior
    }
    return input.isSenior || state.isPartnerSenior
  }

  return (
    <FormContainer fullWidth>
      <form>
        <Box fullWidth fullPadding spacing="16px">
          <Box direction="row" gap="15px">
            {(state.isRegistrationCompleted || !!state.obfuscatedEnrollment?.partnerUser?.phone) && (
              <img src={iconCompleted} width={24} height={24} />
            )}
            <GrooverText textKey="registrationHeadersAndStatusHeaderPartner" />
          </Box>
          <Box fullWidth top bottom spacing="8px">
            <Controller
              control={formProps.control}
              name="partnerName"
              render={({ field, fieldState }) => (
                <TextFieldWithLabelForm
                  disabled={
                    state.isRegistrationCompleted ||
                    state.isRegistrationSubmitted ||
                    !!state.obfuscatedEnrollment?.partnerUser?.name
                  }
                  {...field}
                  value={
                    state.obfuscatedEnrollment?.partnerUser?.name
                      ? state.obfuscatedEnrollment?.partnerUser?.name
                      : field.value
                  }
                  fullWidth
                  fieldState={fieldState}
                  placeholder={getGrooverText('registrationStep1AttendeesRegNameHelper')}
                  autoComplete="name"
                />
              )}
            />
          </Box>
          <Box fullWidth top bottom spacing="8px">
            <Controller
              control={formProps.control}
              name="partnerPhoneNumber"
              render={({ field, fieldState }) => (
                <PhoneInputWithFlag
                  name="partnerPhoneNumber"
                  client={client}
                  disabled={
                    state.isRegistrationCompleted ||
                    state.isRegistrationSubmitted ||
                    !!state.obfuscatedEnrollment?.partnerUser?.phone
                  }
                  value={
                    state.obfuscatedEnrollment?.partnerUser?.phone
                      ? state.obfuscatedEnrollment?.partnerUser?.phone
                      : field.value
                  }
                  onChange={field.onChange}
                  onCountryChange={onPartnerPhoneCountryChange}
                  fieldState={fieldState}
                  placeholder={getGrooverText('registrationStep1AttendeesRegPhoneNumberHelper')}
                  autoComplete="tel"
                  fullWidth
                />
              )}
            />
          </Box>
          <Box fullWidth top bottom spacing="8px">
            <Controller
              control={formProps.control}
              name="partnerEmail"
              rules={{
                validate: (email) => {
                  if (!email) return false
                  return isValidEmail(email)
                }
              }}
              render={({ field, fieldState }) => (
                <TextFieldWithLabelForm
                  type="email"
                  disabled={
                    state.isRegistrationCompleted ||
                    state.isRegistrationSubmitted ||
                    !!state.obfuscatedEnrollment?.partnerUser?.email
                  }
                  {...field}
                  value={
                    state.obfuscatedEnrollment?.partnerUser?.email
                      ? state.obfuscatedEnrollment?.partnerUser?.email
                      : field.value
                  }
                  fullWidth
                  fieldState={fieldState}
                  placeholder={getGrooverText('registrationStep1AttendeesRegEmailHelper')}
                  autoComplete="email"
                />
              )}
            />
          </Box>

          {!!client?.showAddressInRegistration && (
            <Box fullWidth top bottom spacing="8px">
              <Controller
                control={formProps.control}
                name="partnerUserAddress"
                render={({ field, fieldState }) => (
                  <TextFieldWithLabelForm
                    disabled={
                      state.isRegistrationCompleted ||
                      state.isRegistrationSubmitted ||
                      !!state.obfuscatedEnrollment?.partnerUser?.address
                    }
                    {...field}
                    value={
                      state.obfuscatedEnrollment?.partnerUser?.address
                        ? state.obfuscatedEnrollment?.partnerUser?.address
                        : field.value
                    }
                    fullWidth
                    fieldState={fieldState}
                    placeholder={getGrooverText('registrationStep1AddressPlaceholder')}
                    autoComplete="street-address"
                  />
                )}
              />
            </Box>
          )}

          {!!client?.showSsnInRegistration && (
            <Box fullWidth top bottom spacing="8px">
              <Controller
                control={formProps.control}
                name="partnerUserSsn"
                render={({ field, fieldState }) => (
                  <TextFieldWithLabelForm
                    disabled={
                      state.isRegistrationCompleted ||
                      state.isRegistrationSubmitted ||
                      !!state.obfuscatedEnrollment?.partnerUser?.ssn
                    }
                    {...field}
                    value={
                      state.obfuscatedEnrollment?.partnerUser?.ssn
                        ? state.obfuscatedEnrollment?.partnerUser?.ssn
                        : field.value
                    }
                    fullWidth
                    fieldState={fieldState}
                    placeholder={t('registrationStep1AttendeesRegSocialSecurityNumber')}
                  />
                )}
              />
            </Box>
          )}

          <Box id="user-types-container" direction="row" fullWidth changeDirectionOnMobile align="center">
            <UserTypeBox id="user-types-roles" spacing={EIGHT_PX} alignSelf="flex-start">
              <FormControl>
                <StyledRadioGroup
                  row
                  value={(state.obfuscatedEnrollment?.partnerUser?.type || partnerRole || '') as string}
                  onChange={(e) => {
                    if (
                      state.isRegistrationCompleted ||
                      state.isRegistrationSubmitted ||
                      state.obfuscatedEnrollment?.partnerUser?.phone
                    )
                      return
                    changeRole(true, e.target.value as DanceRoles)
                  }}
                >
                  <StyledFormControlLabel
                    value={DanceRoles.Leader}
                    control={<StyledRadio />}
                    label={getGrooverText('registrationStep1AttendeesRegRoleLabelLeader')}
                    disabled={
                      state.isRegistrationCompleted ||
                      state.isRegistrationSubmitted ||
                      !!state.obfuscatedEnrollment?.partnerUser?.phone
                    }
                  />
                  <StyledFormControlLabel
                    value={DanceRoles.Follower}
                    control={<StyledRadio />}
                    label={getGrooverText('registrationStep1AttendeesRegRoleLabelFollower')}
                    disabled={
                      state.isRegistrationCompleted ||
                      state.isRegistrationSubmitted ||
                      !!state.obfuscatedEnrollment?.partnerUser?.phone
                    }
                  />
                  {!!client?.allowBothForCouples && (
                    <StyledFormControlLabel
                      value={DanceRoles.Both}
                      control={<StyledRadio />}
                      label={getGrooverText('registrationStep1AttendeesRegRoleLabelBoth')}
                      disabled={
                        state.isRegistrationCompleted ||
                        state.isRegistrationSubmitted ||
                        !!state.obfuscatedEnrollment?.partnerUser?.phone
                      }
                    />
                  )}
                </StyledRadioGroup>
              </FormControl>
              {!!formProps.formState.errors.partnerRole?.message && (
                <RoleErrorText>{String(formProps.formState.errors.partnerRole.message)}</RoleErrorText>
              )}
            </UserTypeBox>
            <UserTypeDiscounts
              id="user-types-discounts"
              direction="row"
              alignSelf="flex-start"
              align="center"
              spacing={TWELVE_PX}
              gap={TWELVE_PX}
            >
              {activityHasStudentDiscount && (
                <Box direction="row" align="center" gap={FOUR_PX}>
                  <Checkbox checked={getStudentStatus()} onClick={handleSelectStudentDiscount} />
                  <GrooverText
                    textKey="registrationStep3ActivitiesStudentCheckboxLabel"
                    customerId={client?.id}
                    style={{ color: 'rgba(0, 11, 97, 1)' }}
                  />
                </Box>
              )}
              {activityHasSiblingDiscount && activity.allowChildRegistration && (
                <Box direction="row" align="center" gap={FOUR_PX}>
                  <Checkbox checked={getSiblingStatus()} onClick={handleSelectSiblingDiscount} />
                  <GrooverText
                    textKey="registrationStep1SiblingDiscountCheckboxLabel"
                    customerId={client?.id}
                    style={{ color: 'rgba(0, 11, 97, 1)' }}
                  />
                </Box>
              )}
              {activityHasSeniorDiscount && (
                <Box direction="row" align="center" gap={FOUR_PX}>
                  <Checkbox checked={getSeniorStatus()} onClick={handleSelectSeniorDiscount} />
                  <GrooverText textKey="registrationStep3ActivitiesSeniorCheckboxLabel" />
                </Box>
              )}
            </UserTypeDiscounts>
          </Box>

          <Box fullWidth direction="row" align="center" justify="space-between" top spacing="8px">
            {!disableRemovePartner && !state.obfuscatedEnrollment?.partnerUser && (
              <Box direction="row" align="center" gap={TWELVE_PX}>
                {!state.isRegistrationCompleted && (
                  <RemovePartnerButton
                    direction="row"
                    align="center"
                    onClick={
                      state.isRegistrationCompleted || state.isRegistrationSubmitted ? undefined : handleRemovePartner
                    }
                  >
                    <Clear sx={{ fontSize: 20, marginRight: '8px' }} />
                    <GrooverText textKey="registrationCtactaRemovePartner" />
                  </RemovePartnerButton>
                )}
              </Box>
            )}
            {!state.isRegistrationCompleted ? (
              <ContinueButton
                isDisabled={
                  isCheckingUnfinishedEnrollment ||
                  state.hasFormErrors ||
                  (!state.obfuscatedEnrollment?.partnerUser?.type && !partnerRole)
                }
                disabled={
                  isCheckingUnfinishedEnrollment ||
                  state.hasFormErrors ||
                  (!state.obfuscatedEnrollment?.partnerUser?.type && !partnerRole)
                }
                onClick={state.obfuscatedEnrollment?.partnerUser?.phone ? onNextStep : formProps.handleSubmit(onSubmit)}
              >
                <GrooverText textKey="registrationCtactaContinue" />
                {isCheckingUnfinishedEnrollment ? <Spinner /> : <img src={iconArrow} width={16} height={16} />}
              </ContinueButton>
            ) : null}
          </Box>
        </Box>
      </form>
    </FormContainer>
  )
}

const UserTypeDiscounts = styled(Box)`
  padding: 4px;
`

const UserTypeBox = styled(Box)`
  background-color: rgb(243, 242, 242);
  border-radius: 12px;
`

const RoleErrorText = styled.span`
  display: block;
  color: #d32f2f;
  padding: 0 12px 8px 12px;
  font-size: 0.875rem;
`

const FormContainer = styled(Box)`
  max-width: 865px;
  border-radius: 12px;
  background: #fcfcfc;
  box-shadow: 0px 0px 8px 0px #2422bb;
`

const StyledRadioGroup = muiStyled(RadioGroup)`
  gap: 6px;
`

const StyledFormControlLabel = muiStyled(FormControlLabel)`
  border-radius: 12px;
  margin: 0;
  padding: 8px 16px;
  color: black;
  
  &.Mui-checked {
    background-color: #0E0349;
    
    .MuiTypography-root {
      color: black;
    }
  }

  .MuiTypography-root {
    color: black;
  }
`

const StyledRadio = muiStyled(Radio)`
  padding: 4px;
`

const RemovePartnerButton = styled(Box)`
  cursor: pointer;
  border: 1px solid #aaa;
  padding: 8px 16px;
  border-radius: ${BorderRadixes.moderate};
  background-color: white;
  color: red;
  display: flex;
  align-items: center;

  &:hover {
    background-color: #f5f5f5;
  }
`

export default memo(CourseRegistrationStepOnePartnerForm)
